import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/orders'
import { updateRoleBasedOnRoute } from '@/utils/environmentConfig'

// Import Views (Lazy Loading für bessere Performance)
// Login und Registrierung
const LoginView = () => import('@/views/auth/Login.vue')
const NotFound = () => import('@/views/NotFound.vue')

// Session-Ansichten
const SessionView = () => import('@/views/SessionView.vue')
const SessionExpired = () => import('@/views/SessionExpired.vue')
const SessionForbidden = () => import('@/views/SessionForbidden.vue')
const EntryPointView = () => import('@/views/EntryPointView.vue')

// Kunden-Ansichten - immer laden, da Customer-Routes auch im Admin-Build verfügbar sein müssen
const importCustomerView = (path) => {
  return () => import(`@/views/customer/${path}.vue`).catch(err => {
    console.error(`Fehler beim Laden der Customer-Komponente: ${path}`, err);
    return import('@/views/NotFound.vue');
  });
}

const HomeView = importCustomerView('HomeView')
const MenuView = importCustomerView('MenuView')
const ProductDetailView = importCustomerView('ProductDetailView')
const CartView = importCustomerView('CartView')
const OrderStatus = importCustomerView('OrderStatus')
const PaymentSuccessView = importCustomerView('PaymentSuccessView')
const PaymentCancelView = importCustomerView('PaymentCancelView')
const RoomServiceView = importCustomerView('RoomServiceView')
const RoomEntryView = importCustomerView('RoomEntryView')
const PreOrderView = importCustomerView('PreOrderView')

// Admin-Ansichten (werden nur bei Admin-Build geladen)
const importAdminView = (path) => {
  // Überprüfen, ob wir im Admin-Modus sind oder auf der Admin-Domain
  const buildTarget = process.env.BUILD_TARGET || 'admin'
  const hostname = window.location.hostname
  const isAdminBuild = buildTarget === 'admin'
  const isAdminDomain = hostname === 'admin.ensu.astra-it.at' || hostname.includes('admin.')

  // Nur im Admin-Modus oder auf der Admin-Domain die Komponente laden
  if (isAdminBuild || isAdminDomain) {
    return () => import(`@/views/admin/${path}.vue`).catch(err => {
      console.error(`Fehler beim Laden der Admin-Komponente: ${path}`, err);
      return import('@/views/NotFound.vue');
    });
  }
  
  // Im Kiosk-Modus eine leere Komponente oder NotFound zurückgeben
  return NotFound;
}

const AdminDashboard = importAdminView('Dashboard')
const AdminProducts = importAdminView('Products')
const AdminProductForm = importAdminView('ProductForm')
const AdminTables = importAdminView('Tables')
const AdminUsers = importAdminView('Users')
const AdminUserForm = importAdminView('UserForm')
const AdminReports = importAdminView('Reports')
const TableDetail = importAdminView('TableDetail')

// Super-Admin-Ansichten (werden nur bei Admin-Build geladen)
const importSuperAdminView = (path) => {
  // Überprüfen, ob wir im Admin-Modus sind oder auf der Admin-Domain
  const buildTarget = process.env.BUILD_TARGET || 'admin'
  const hostname = window.location.hostname
  const isAdminBuild = buildTarget === 'admin'
  const isAdminDomain = hostname === 'admin.ensu.astra-it.at' || hostname.includes('admin.')

  // Nur im Admin-Modus oder auf der Admin-Domain die Komponente laden
  if (isAdminBuild || isAdminDomain) {
    return () => import(`@/views/superadmin/${path}.vue`).catch(err => {
      console.error(`Fehler beim Laden der SuperAdmin-Komponente: ${path}`, err);
      return import('@/views/NotFound.vue');
    });
  }
  
  // Im Kiosk-Modus eine leere Komponente oder NotFound zurückgeben
  return NotFound;
}

const SuperAdminDashboard = importSuperAdminView('Dashboard')
const SuperAdminRestaurants = importSuperAdminView('Restaurants')
const SuperAdminRestaurantForm = importSuperAdminView('RestaurantForm')

// Importiere eine Komponente aus einem beliebigen Pfad, nur wenn im Admin-Modus
const importComponentIfAdmin = (fullPath) => {
  const buildTarget = process.env.BUILD_TARGET || 'admin'
  const hostname = window.location.hostname
  const isAdminBuild = buildTarget === 'admin'
  const isAdminDomain = hostname === 'admin.ensu.astra-it.at' || hostname.includes('admin.')

  if (isAdminBuild || isAdminDomain) {
    return () => import(`@/${fullPath}`).catch(err => {
      console.error(`Fehler beim Laden der Komponente: ${fullPath}`, err);
      return import('@/views/NotFound.vue');
    });
  }
  
  return NotFound;
};

// Küchen-Ansicht (nur im Admin-Build) - Korrigierter Pfad
const KitchenView = importComponentIfAdmin('views/kitchen/KitchenView.vue');

// Waiter View (nur im Admin-Build) - Angepasst für Konsistenz
const WaiterDashboard = importComponentIfAdmin('components/WaiterDashboard.vue');

// Bestimmt, welche Routen basierend auf Build-Typ und Domain geladen werden
const getBuildSpecificRoutes = () => {
  const buildTarget = process.env.BUILD_TARGET || 'admin'
  const hostname = window.location.hostname
  
  // Auf Basis des Hostnamens erkennen, welche Routen geladen werden sollen
  const isKioskDomain = hostname === 'kiosk.ensu.astra-it.at' || hostname.includes('kiosk.')
  const isAdminDomain = hostname === 'admin.ensu.astra-it.at' || hostname.includes('admin.')
  
  // Final entscheiden: Domain hat Vorrang vor BUILD_TARGET
  const routeType = isKioskDomain ? 'kiosk' : 
                    isAdminDomain ? 'admin' : 
                    buildTarget
  
  console.log(`Router wird für Modus "${routeType}" initialisiert. Hostname: ${hostname}, BUILD_TARGET: ${buildTarget}`)
  
  return routeType
}

// Public Routes (gemeinsam für Admin und Kiosk)
export const publicRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false, isPublic: true }
  },
  // NFC-Zugriff
  {
    path: '/nfc/:tableId',
    name: 'NFCAccess',
    component: EntryPointView,
    meta: { requiresAuth: false }
  },
  // QR-Code-Zugriff
  {
    path: '/qr/:tableId',
    name: 'QRAccess',
    component: EntryPointView,
    meta: { requiresAuth: false }
  },
  // Sichere Session-Route
  {
    path: '/s/:sessionPath',
    name: 'SecureSessionView',
    component: SessionView,
    meta: { requiresAuth: false }
  },
  // Session abgelaufen
  {
    path: '/abgelaufen',
    name: 'SessionExpired',
    component: SessionExpired,
    meta: { requiresAuth: false }
  },
  // Session nicht erlaubt
  {
    path: '/nicht-erlaubt',
    name: 'SessionForbidden',
    component: SessionForbidden,
    meta: { requiresAuth: false }
  }
]

// Customer Routes (nur für Kiosk, HomeView ist sowohl default für / als auch für Table-View)
export const customerRoutes = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    meta: { requiresAuth: false, isPublic: true }
  },
  {
    path: '/table/:tableId/:sessionId',
    name: 'TableView',
    component: HomeView,
    meta: { requiresAuth: false }
  },
  {
    path: '/menu/:tableId/:sessionId',
    component: MenuView,
    meta: { requiresAuth: false }
  },
  {
    path: '/menu',
    component: MenuView,
    meta: { requiresAuth: false }
  },
  {
    path: '/product/:productId',
    component: ProductDetailView,
    props: route => ({ 
      productId: Number(route.params.productId),
      tableId: Number(route.query.tableId) || null,
      sessionUniqueId: route.query.sessionUniqueId || null,
      orderType: route.query.orderType || 'eat-in'
    }),
    meta: { requiresAuth: false }
  },
  {
    path: '/cart',
    component: CartView,
    meta: { requiresAuth: false }
  },
  {
    path: '/cart/:tableId/:sessionId',
    component: CartView,
    meta: { requiresAuth: false }
  },
  {
    path: '/room/:roomNumber?',
    name: 'RoomService',
    component: RoomServiceView,
    meta: { requiresAuth: false, isPublic: true }
  },
  {
    path: '/r/:roomNumber?',
    name: 'RoomServiceShort',
    component: RoomServiceView,
    meta: { requiresAuth: false, isPublic: true }
  },
  {
    path: '/scan/:token',
    name: 'RoomEntry',
    component: RoomEntryView,
    meta: { requiresAuth: false, isPublic: true },
  },
  {
    path: '/preorder',
    name: 'CustomerPreOrder',
    component: PreOrderView,
    meta: { requiresAuth: false, isPublic: true },
  },
  {
    path: '/embed/live-occupancy',
    name: 'LiveOccupancyEmbed',
    component: () => import('@/views/public/LiveOccupancyEmbed.vue'),
    meta: { requiresAuth: false, isPublic: true, layout: 'none' },
  },
  {
    path: '/payment-success',
    component: PaymentSuccessView,
    meta: { requiresAuth: false }
  },
  {
    path: '/payment-cancel',
    component: PaymentCancelView,
    meta: { requiresAuth: false }
  },
  {
    path: '/table/:tableId/:sessionId/orders',
    name: 'OrderStatus',
    component: OrderStatus,
    meta: { requiresAuth: false }
  },
  {
    path: '/order-status',
    name: 'CustomerOrders',
    component: OrderStatus,
    meta: { requiresAuth: false }
  }
]

// Admin Routes
export const adminRoutes = {
  path: '/admin',
  redirect: '/admin/dashboard',
  meta: { 
    requiresAuth: true,
    // Alle internen Rollen können in den Admin-Bereich; Detail-Routen schränken weiter ein.
    role: ['admin', 'restaurant_admin', 'manager', 'hotel_group_admin', 'superadmin', 'kitchen', 'waiter', 'staff']
  },
  children: [
    {
      path: 'dashboard',
      name: 'Dashboard',
      component: AdminDashboard,
      meta: { role: ['admin', 'restaurant_admin', 'manager', 'superadmin'] },
    },
    {
      path: 'orders',
      name: 'Orders',
      component: importAdminView('OrdersV2'),
    },
    {
      path: 'tables',
      name: 'Tables',
      component: AdminTables
    },
    {
      path: 'tables/:id',
      name: 'TableDetail',
      component: TableDetail,
      props: true
    },
    {
      path: 'users',
      name: 'Users',
      component: AdminUsers
    },
    {
      path: 'users/new',
      name: 'UserNew',
      component: AdminUserForm
    },
    {
      path: 'users/:id',
      name: 'UserEdit',
      component: AdminUserForm,
      props: true
    },
    {
      path: 'products',
      name: 'Products',
      component: AdminProducts
    },
    {
      path: 'products/new',
      name: 'ProductNew',
      component: AdminProductForm
    },
    {
      path: 'products/:id',
      name: 'ProductEdit',
      component: AdminProductForm,
      props: true
    },
    {
      path: 'reports',
      name: 'Reports',
      component: AdminReports
    },
    {
      path: 'categories',
      name: 'Categories',
      component: importAdminView('Categories')
    },
    {
      path: 'extras',
      name: 'Extras',
      component: importAdminView('Extras')
    },
    {
      path: 'settings',
      name: 'Settings',
      component: importAdminView('Settings')
    },
    {
      path: 'kitchen',
      name: 'AdminKitchen',
      component: KitchenView,
      meta: { role: ['admin', 'manager', 'kitchen'] },
    },
    {
      path: 'waiter-dashboard',
      name: 'AdminWaiterDashboard',
      component: WaiterDashboard,
      meta: { role: ['admin', 'manager', 'waiter'] }
    },
    {
      path: 'gamification',
      name: 'AdminGamification',
      component: () => import('@/views/admin/Gamification.vue'),
    },
    {
      path: 'room-charges',
      name: 'AdminRoomCharges',
      component: importAdminView('RoomCharges'),
    },
    {
      path: 'hotel-dashboard',
      name: 'AdminHotelDashboard',
      component: importAdminView('HotelDashboard'),
      meta: { role: ['admin', 'manager', 'hotel_group_admin'] },
    },
    {
      path: 'group-dashboard',
      name: 'AdminHotelGroupDashboard',
      component: importAdminView('HotelGroupDashboard'),
      // Konzern-Dashboard ist bewusst NUR fuer Konzernzentrale + Plattformbetreiber.
      // Ein einzelner Hotel-Admin sieht hier nichts (Cross-Property-Datenschutz).
      meta: { role: ['hotel_group_admin', 'superadmin'] },
    },
    {
      path: 'group-users',
      name: 'AdminHotelGroupUsers',
      component: importAdminView('HotelGroupUsers'),
      meta: { role: ['hotel_group_admin', 'superadmin'] },
    },
    {
      path: 'hotel-locations',
      name: 'AdminHotelLocations',
      component: importAdminView('HotelLocations'),
      meta: { role: ['admin', 'manager'] },
    },
    {
      path: 'waiter-pos',
      name: 'AdminWaiterPos',
      component: importAdminView('WaiterPos'),
      meta: { role: ['admin', 'manager', 'staff', 'waiter'], layout: 'fullscreen' },
    },
    {
      path: 'waiter',
      name: 'AdminWaiter',
      component: importAdminView('WaiterApp'),
      meta: { role: ['admin', 'manager', 'staff', 'waiter'], layout: 'fullscreen' },
    },
    {
      path: 'rooms',
      name: 'AdminHotelRooms',
      component: importAdminView('HotelRooms'),
      meta: { role: ['admin', 'manager'] },
    },
    {
      path: 'pre-orders',
      name: 'AdminPreOrders',
      component: importAdminView('PreOrders'),
      meta: { role: ['admin', 'manager', 'staff'] },
    },
    {
      path: 'locations',
      name: 'AdminLocations',
      component: importAdminView('Locations'),
      meta: { role: ['admin', 'manager'] },
    },
    {
      path: 'battle-royale',
      name: 'AdminBattleRoyale',
      component: () => import('@/views/admin/BattleRoyaleAdmin.vue'),
    }
  ]
}

// SuperAdmin Routes
export const superAdminRoutes = {
  path: '/superadmin',
  redirect: '/superadmin/dashboard',
  meta: { requiresSuperAdmin: true },
  children: [
    {
      path: 'dashboard',
      name: 'SuperAdminDashboard',
      component: SuperAdminDashboard
    },
    {
      path: 'restaurants',
      name: 'SuperAdminRestaurants',
      component: SuperAdminRestaurants
    },
    {
      path: 'onboarding',
      name: 'SuperAdminOnboarding',
      component: importSuperAdminView('Onboarding'),
    },
    {
      path: 'restaurants/new',
      name: 'SuperAdminRestaurantNew',
      component: SuperAdminRestaurantForm
    },
    {
      path: 'restaurants/:id',
      name: 'SuperAdminRestaurantEdit',
      component: SuperAdminRestaurantForm,
      props: true
    },
    {
      path: 'restaurants/details/:id',
      name: 'SuperAdminRestaurantDetail',
      component: importSuperAdminView('RestaurantDetail'),
      props: true
    },
    {
      path: 'gamification',
      name: 'SuperAdminGamification',
      component: () => import('@/views/admin/Gamification.vue'),
    }
  ]
}

// Staff Routes (Kitchen, Waiter)
export const staffRoutes = [
  {
    path: '/kitchen',
    name: 'Kitchen',
    component: KitchenView,
    meta: { requiresAuth: true, role: ['kitchen', 'staff', 'admin', 'manager'] }
  },
  {
    path: '/waiter-dashboard',
    name: 'WaiterDashboard',
    component: WaiterDashboard,
    meta: { requiresAuth: true, role: ['waiter', 'admin', 'manager'] }
  }
]

// Combine routes based on build type
const routeType = getBuildSpecificRoutes()

// Basierend auf dem Build-Typ/Domain die Routen auswählen
let routes = []

// Public Routes immer einschließen
routes.push(...publicRoutes)

// Kiosk-Build oder Kiosk-Domain
if (routeType === 'kiosk') {
  routes.push(...customerRoutes)
  
  // Home-Route für Kiosk auf '/' setzen
  const homeRoute = routes.find(r => r.name === 'Home')
  if (homeRoute) {
    homeRoute.component = HomeView
  }
} 
// Admin-Build oder Admin-Domain
else {
  routes.push(...customerRoutes) // Auch im Admin möchten wir die Customer-Routen haben
  routes.push(adminRoutes)
  routes.push(superAdminRoutes)
  routes.push(...staffRoutes)
  
  // Home-Route für Admin auf '/admin/dashboard' umleiten
  const homeRoute = routes.find(r => r.name === 'Home')
  if (homeRoute) {
    homeRoute.redirect = '/admin/dashboard'
  }
}

// 404-Route hinzufügen
routes.push({
  path: '/:pathMatch(.*)*', // Catch-all 404
  name: 'NotFound',
  component: NotFound
})

const router = createRouter({
  history: createWebHistory(),
  routes: routes
})

// Globale Router-Instanz für den Zugriff in WebSocket-Handlern
window._vueRouter = router;

// Globaler Navigation Guard
router.beforeEach((to, from, next) => {
  try {
    // Aktualisiere die Rolle basierend auf der Route
    updateRoleBasedOnRoute(to.path)

    // Login-Route immer erlauben, ohne Rollenprüfung
    if (to.name === 'Login') {
      next()
      return
    }

    // Authentifizierung prüfen, falls die Route es erfordert
    const authStore = useAuthStore()
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    const requiresSuperAdmin = to.matched.some(record => record.meta.requiresSuperAdmin)

    if (requiresAuth && !authStore.isAuthenticated) {
      console.log('[router] not authenticated → /login')
      next({ name: 'Login', query: { redirect: to.fullPath } })
      return
    }

    if (requiresSuperAdmin) {
      if (!authStore.isAuthenticated || !authStore.hasSuperAdminRole) {
        console.log('[router] superadmin required but missing → /login')
        next({ name: 'Login' })
        return
      }
    }

    // Wir merken uns die *tiefste* (= zuletzt gematchte) `meta.role`-Definition.
    // Das vermeidet, dass der breite Parent-Array-Wert (`/admin`) eine engere
    // Child-Beschränkung überschreibt — und umgekehrt, dass eine fehlende
    // Child-Definition keine fälschliche Sperre erzeugt.
    let routeRole = null
    for (const record of to.matched) {
      if (record.meta && record.meta.role !== undefined) {
        routeRole = record.meta.role
      }
    }

    if (routeRole && authStore.isAuthenticated) {
      const userRole = authStore.user?.role?.toLowerCase()
      const requiredRole = Array.isArray(routeRole)
        ? routeRole.map(r => String(r).toLowerCase())
        : String(routeRole).toLowerCase()

      const allowed = userRole && (Array.isArray(requiredRole)
        ? requiredRole.includes(userRole)
        : requiredRole === userRole)

      if (!allowed) {
        const fallbackByRole = {
          superadmin: '/superadmin/dashboard',
          hotel_group_admin: '/admin/group-dashboard',
          admin: '/admin/dashboard',
          restaurant_admin: '/admin/dashboard',
          manager: '/admin/dashboard',
          kitchen: '/admin/kitchen',
          waiter: '/admin/waiter',
          staff: '/admin/waiter',
        }
        const fallback = fallbackByRole[userRole] || '/'

        // Endlosschleifen-Schutz: NIEMALS next(false) — sonst weißes Fenster.
        if (to.path === fallback || from.path === fallback) {
          console.warn(`[router] role "${userRole}" not allowed for ${to.path}; staying on ${from.fullPath}`)
          next(from.fullPath || '/')
          return
        }

        console.log(`[router] role "${userRole}" not allowed for ${to.path} → ${fallback}`)
        next({ path: fallback })
        return
      }
    }

    // Wenn der Benutzer zum Waiter-Dashboard navigiert, Benachrichtigungen zurücksetzen
    if (to.path === '/waiter-dashboard' || to.path === '/admin/waiter-dashboard') {
      const orderStore = useOrderStore()
      orderStore.clearWaiterNotifications()
    }

    next()
  } catch (err) {
    // Im Fehlerfall NIE die Navigation hängen lassen — sonst weißes Fenster.
    console.error('[router] guard error, allowing navigation anyway:', err)
    next()
  }
})

// Falls eine Lazy-Component-Auflösung fehlschlägt (z. B. nach Hot-Reload mit
// alten Chunks), Seite neu laden statt mit weißem Fenster steckenzubleiben.
router.onError((err) => {
  const msg = String(err?.message || err)
  if (/Loading chunk \S+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(msg)) {
    console.warn('[router] chunk load failed, full reload:', msg)
    window.location.reload()
  } else {
    console.error('[router] navigation error:', err)
  }
})

// Update bei Route-Änderung
router.afterEach((to) => {
  // Aktualisiere die Rolle basierend auf der Route
  updateRoleBasedOnRoute(to.path);
});

export default router 